import React, { useCallback, useMemo } from 'react'
import { Button } from 'antd'
import { includes, isEmpty, isNil } from 'lodash'
import PropTypes from 'prop-types'

import { USER_ASSIGNMENT_ACTION_TYPE } from '../../../common/constants'

import { MENU_CURATION_TASK_STATUS } from '../../constants'
import {
  useMenuCurationTaskAllocatedUsers,
  useMenuCurationTaskAssignUserDisabledFlags,
  useRemoveFromAllocationMutation,
} from '../../hooks'
import { menuCurationTaskPropType } from '../../prop-types'
import { StyledAllocatedUserLabel } from '../styled'

import MenuCurationTaskUserAssignment from './menu-curation-task-user-assignment'

const MenuCurationTaskTableAssignation = ({ menuCurationTask, isDisabled }) => {
  const {
    id,
    assignedTierOneCurator,
    assignedCurator,
    assignedQa,
    assignedQc,
    assignedUnknownWordsCurator,
    assignedUnknownWordsQa,
    assignedScrapingReviewerUser,
    taskStatus,
  } = menuCurationTask

  const {
    isUnknownWordsCurationAssignationDisabled,
    isUnknownWordsQaAssignationDisabled,
    isTierOneAssignationDisabled,
    isCuratorAssignationDisabled,
    isQaAssignationDisabled,
    isQcAssignationDisabled,
    isScrapingReviewUserAssignationDisabled,
  } = useMenuCurationTaskAssignUserDisabledFlags(menuCurationTask, isDisabled)

  const {
    isCurrentUserAllocated,
    allocatedCurator,
    allocatedQa,
    allocatedQc,
    allocatedTierOneCurator,
  } = useMenuCurationTaskAllocatedUsers(menuCurationTask)

  const [removeFromAllocation] = useRemoveFromAllocationMutation(
    menuCurationTask.id,
  )

  const handleRemoveFromAllocation = useCallback(() => {
    removeFromAllocation()
  }, [removeFromAllocation])

  const isScrapingReviewPending = useMemo(
    () =>
      includes(
        [
          MENU_CURATION_TASK_STATUS.SCRAPING_REVIEW_IN_PROGRESS.value,
          MENU_CURATION_TASK_STATUS.AWAITING_SCRAPING_REVIEW.value,
          MENU_CURATION_TASK_STATUS.AWAITING_AUTOEAT_UPDATES.value,
          MENU_CURATION_TASK_STATUS.NEEDS_SCRAPING_CLARIFICATION.value,
        ],
        taskStatus,
      ),
    [taskStatus],
  )

  const alreadyAssignedUsersDuringCuration = useMemo(
    () => [assignedQc?.id],
    [assignedQc],
  )

  const alreadyAssignedUsersDuringQa = useMemo(
    () => [assignedQc?.id],
    [assignedQc],
  )

  const alreadyAssignedUsersDuringQc = useMemo(
    () => [assignedCurator?.id, assignedQa?.id],
    [assignedCurator, assignedQa],
  )

  const isAllocatedCuratorLabelVisible = useMemo(
    () =>
      !isEmpty(allocatedCurator) &&
      !isCurrentUserAllocated &&
      isNil(assignedCurator),
    [allocatedCurator, assignedCurator, isCurrentUserAllocated],
  )

  const isAllocatedTierOneLabelVisible = useMemo(
    () =>
      !isEmpty(allocatedTierOneCurator) &&
      !isCurrentUserAllocated &&
      isNil(assignedTierOneCurator),
    [allocatedTierOneCurator, assignedTierOneCurator, isCurrentUserAllocated],
  )

  const isAllocatedQaLabelVisible = useMemo(
    () => !isEmpty(allocatedQa) && !isCurrentUserAllocated && isNil(assignedQa),
    [allocatedQa, assignedQa, isCurrentUserAllocated],
  )

  const isAllocatedQcLabelVisible = useMemo(
    () => !isEmpty(allocatedQc) && !isCurrentUserAllocated && isNil(assignedQc),
    [allocatedQc, assignedQc, isCurrentUserAllocated],
  )

  return (
    <>
      {!isScrapingReviewPending && (
        <>
          {isAllocatedTierOneLabelVisible ? (
            <StyledAllocatedUserLabel type="secondary">
              Allocated tier one curator: {allocatedTierOneCurator.name}
            </StyledAllocatedUserLabel>
          ) : (
            <MenuCurationTaskUserAssignment
              tooltip="Tier One Curator"
              menuCurationTaskId={id}
              userActionType={USER_ASSIGNMENT_ACTION_TYPE.TIER_ONE}
              assignedUser={assignedTierOneCurator}
              otherAssignedUsers={[]}
              isDisabled={isTierOneAssignationDisabled}
              isCheckbox
            />
          )}

          {isAllocatedCuratorLabelVisible ? null : (
            <MenuCurationTaskUserAssignment
              tooltip="U.W. Curator"
              menuCurationTaskId={id}
              userActionType={
                USER_ASSIGNMENT_ACTION_TYPE.UNKNOWN_WORDS_CURATION
              }
              assignedUser={assignedUnknownWordsCurator}
              otherAssignedUsers={[]}
              isDisabled={isUnknownWordsCurationAssignationDisabled}
              isCheckbox
            />
          )}

          {isAllocatedQaLabelVisible ? null : (
            <MenuCurationTaskUserAssignment
              tooltip="U.W. Qa"
              menuCurationTaskId={id}
              userActionType={USER_ASSIGNMENT_ACTION_TYPE.UNKNOWN_WORDS_QA}
              assignedUser={assignedUnknownWordsQa}
              otherAssignedUsers={[]}
              isDisabled={isUnknownWordsQaAssignationDisabled}
              isCheckbox
            />
          )}

          {isAllocatedCuratorLabelVisible ? (
            <StyledAllocatedUserLabel type="secondary">
              Allocated curator: {allocatedCurator.name}
            </StyledAllocatedUserLabel>
          ) : (
            <MenuCurationTaskUserAssignment
              tooltip="Curation"
              menuCurationTaskId={id}
              userActionType={USER_ASSIGNMENT_ACTION_TYPE.CURATION}
              isDisabled={isCuratorAssignationDisabled}
              assignedUser={assignedCurator}
              otherAssignedUsers={alreadyAssignedUsersDuringCuration}
              isCheckbox
            />
          )}

          {isAllocatedQaLabelVisible ? (
            <StyledAllocatedUserLabel type="secondary">
              Allocated QA: {allocatedQa.name}
            </StyledAllocatedUserLabel>
          ) : (
            <MenuCurationTaskUserAssignment
              tooltip="Qa"
              menuCurationTaskId={id}
              userActionType={USER_ASSIGNMENT_ACTION_TYPE.QA}
              isDisabled={isQaAssignationDisabled}
              assignedUser={assignedQa}
              otherAssignedUsers={alreadyAssignedUsersDuringQa}
              isCheckbox
            />
          )}

          {isAllocatedQcLabelVisible ? (
            <StyledAllocatedUserLabel type="secondary">
              Allocated QC: {allocatedQc.name}
            </StyledAllocatedUserLabel>
          ) : (
            <MenuCurationTaskUserAssignment
              tooltip="Qc"
              menuCurationTaskId={id}
              userActionType={USER_ASSIGNMENT_ACTION_TYPE.QC}
              isDisabled={isQcAssignationDisabled}
              assignedUser={assignedQc}
              otherAssignedUsers={alreadyAssignedUsersDuringQc}
              isCheckbox
            />
          )}
        </>
      )}

      {isScrapingReviewPending && (
        <MenuCurationTaskUserAssignment
          tooltip="Scraping Reviewer"
          menuCurationTaskId={id}
          userActionType={USER_ASSIGNMENT_ACTION_TYPE.SCRAPING_REVIEW}
          isDisabled={isScrapingReviewUserAssignationDisabled}
          assignedUser={assignedScrapingReviewerUser}
          isCheckbox
        />
      )}

      {isCurrentUserAllocated && (
        <Button type="link" onClick={handleRemoveFromAllocation}>
          Remove from allocation
        </Button>
      )}
    </>
  )
}

MenuCurationTaskTableAssignation.propTypes = {
  menuCurationTask: menuCurationTaskPropType.isRequired,
  isDisabled: PropTypes.bool,
}

MenuCurationTaskTableAssignation.defaultProps = {
  isDisabled: false,
}

export default MenuCurationTaskTableAssignation
